import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Save, Upload } from "lucide-react";
import { toast } from "sonner";
import { adminPartners, adminUploads, formatError } from "../api";
import { Button, Card, Input, PageHeader, Spinner, Switch } from "../ui";

const CATEGORIES = ["government", "airport", "international", "private", "training", "development"];

const empty = {
  name: "",
  category: "",
  logo_url: "",
  website_url: "",
  display_order: 0,
  published: true,
};

export default function PartnerEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const isNew = !id;
  const [draft, setDraft] = useState(isNew ? empty : null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (isNew) return;
    adminPartners.get(id).then(setDraft).catch(() => setDraft(false));
  }, [id, isNew]);

  if (draft === false) return <p className="text-sm text-red-600">Partner not found.</p>;
  if (!draft) return <Spinner />;

  const setField = (k) => (v) => setDraft({ ...draft, [k]: v });

  const onUploadLogo = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const r = await adminUploads.upload(f, "partner_logo");
      setDraft({ ...draft, logo_url: r.url });
      toast.success("Logo uploaded.");
    } catch (err) { toast.error(formatError(err)); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const save = async () => {
    if (!draft.name || draft.name.trim().length < 2) return toast.error("Name is required");
    setBusy(true);
    try {
      const payload = { ...draft, display_order: Number(draft.display_order || 0) };
      const res = isNew ? await adminPartners.create(payload) : await adminPartners.update(id, payload);
      toast.success(isNew ? "Partner created." : "Partner saved.");
      if (isNew) nav(`/admin/partners/${res.id}`, { replace: true });
      else setDraft(res);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-partner-editor">
      <Link to="/admin/partners" className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-[0.12em] text-[hsl(var(--aoa-royal))] mb-4"><ArrowLeft className="h-3.5 w-3.5" /> All partners</Link>
      <PageHeader
        title={isNew ? "New Partner" : "Edit Partner"}
        actions={<Button onClick={save} loading={busy} testId="partner-save"><Save className="h-3.5 w-3.5" /> {isNew ? "Create" : "Save"}</Button>}
      />
      <div className="grid gap-6 lg:grid-cols-[1.3fr_1fr]">
        <Card className="p-6 space-y-4">
          <Input label="Name" value={draft.name} onChange={(e) => setField("name")(e.target.value)} testId="partner-name" />
          <div>
            <span className="mb-1.5 inline-block text-xs font-semibold uppercase tracking-[0.12em] text-slate-700">Category</span>
            <select
              value={draft.category || ""}
              onChange={(e) => setField("category")(e.target.value)}
              className="block w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
              data-testid="partner-category"
            >
              <option value="">— None —</option>
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <Input label="Website URL" value={draft.website_url || ""} onChange={(e) => setField("website_url")(e.target.value)} placeholder="https://…" testId="partner-website" />
          <Input label="Display order" type="number" value={String(draft.display_order ?? 0)} onChange={(e) => setField("display_order")(e.target.value)} hint="Lower numbers show first." testId="partner-order" />
        </Card>

        <div className="space-y-4">
          <Card className="p-6">
            <h3 className="font-display text-base font-semibold text-[hsl(var(--aoa-navy))] mb-3">Visibility</h3>
            <div className="flex items-center justify-between">
              <div className="text-sm">{draft.published ? "Published" : "Hidden"}</div>
              <Switch checked={!!draft.published} onChange={setField("published")} testId="partner-published" />
            </div>
          </Card>
          <Card className="p-6">
            <h3 className="font-display text-base font-semibold text-[hsl(var(--aoa-navy))] mb-3">Logo</h3>
            {draft.logo_url && (
              <div className="grid h-24 place-items-center rounded-lg border border-slate-200 bg-slate-50 mb-3">
                <img src={draft.logo_url} alt="" className="max-h-20 max-w-full object-contain" />
              </div>
            )}
            <Input value={draft.logo_url || ""} onChange={(e) => setField("logo_url")(e.target.value)} placeholder="/api/files/partner_logo/..." testId="partner-logo-url" />
            <label className="mt-3 inline-flex items-center gap-2 rounded-lg border border-dashed border-slate-300 px-3 py-2 text-xs font-semibold text-slate-700 cursor-pointer hover:border-[hsl(var(--aoa-royal))]">
              <Upload className="h-3.5 w-3.5" />
              {uploading ? "Uploading..." : "Upload logo"}
              <input type="file" hidden accept="image/*" onChange={onUploadLogo} data-testid="partner-logo-file" />
            </label>
          </Card>
        </div>
      </div>
    </div>
  );
}
